import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiCall } from '../utils/api';

function ViewRunbook() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [runbook, setRunbook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchRunbookData = async () => {
      try {
        setLoading(true);
        const data = await apiCall(`/runbooks/${id}`);
        console.log('[ViewRunbook] Fetched runbook data:', data);
        
        // Debug: Log steps and image information
        if (data.Steps && data.Steps.length > 0) {
          console.log(`[ViewRunbook] Found ${data.Steps.length} steps`);
          data.Steps.forEach((step, idx) => {
            console.log(`[ViewRunbook] Step ${idx + 1}:`, {
              step_number: step.step_number,
              has_image_url: !!step.image_url,
              has_image_s3_key: !!step.image_s3_key,
              image_url_preview: step.image_url ? step.image_url.substring(0, 100) + '...' : 'none',
              image_s3_key: step.image_s3_key || 'none'
            });
          });
        }
        
        setRunbook(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching runbook:', error);
        setError(error.message || 'Failed to load runbook');
        setLoading(false);
      }
    };

    fetchRunbookData();
  }, [id]);

  if (loading) {
    return (
      <div className="container-fluid mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading runbook details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid mt-4">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Error</h4>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/runbook_list')}>
            Back to Runbook List
          </button>
        </div>
      </div>
    );
  }

  if (!runbook) {
    return (
      <div className="container-fluid mt-4">
        <div className="alert alert-warning" role="alert">
          <p>Runbook not found</p>
          <button className="btn btn-primary" onClick={() => navigate('/runbook_list')}>
            Back to Runbook List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid mt-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>View Runbook</h2>
        <button 
          className="btn btn-secondary"
          onClick={() => navigate('/runbook_list')}
        >
          Back to Runbook List
        </button>
      </div>

      {/* Basic Information */}
      <div className="card mb-4">
        <div className="card-header bg-primary text-white">
          <h5 className="mb-0">Basic Information</h5>
        </div>
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label"><strong>Runbook ID</strong></label>
              <input
                type="text"
                className="form-control bg-light"
                value={runbook['Runbook ID'] || ''}
                readOnly
              />
            </div>
            <div className="col-md-6">
              <label className="form-label"><strong>Modified By</strong></label>
              <input
                type="text"
                className="form-control bg-light"
                value={runbook.ModifiedBy || 'Unknown'}
                readOnly
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label"><strong>Tribe</strong></label>
              <input
                type="text"
                className="form-control bg-light"
                value={runbook.Tribe || ''}
                readOnly
              />
            </div>
            <div className="col-md-6">
              <label className="form-label"><strong>Solution</strong></label>
              <input
                type="text"
                className="form-control bg-light"
                value={runbook.Solution || ''}
                readOnly
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-md-6">
              <label className="form-label"><strong>Runbook Title</strong></label>
              <input
                type="text"
                className="form-control bg-light"
                value={runbook['Runbook Title'] || ''}
                readOnly
              />
            </div>
            <div className="col-md-6">
              <label className="form-label"><strong>Modified Date</strong></label>
              <input
                type="text"
                className="form-control bg-light"
                value={runbook.ModifiedDate || ''}
                readOnly
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="form-label"><strong>Tags</strong></label>
            <input
              type="text"
              className="form-control bg-light"
              value={Array.isArray(runbook.Tags) ? runbook.Tags.join(', ') : runbook.Tags || ''}
              readOnly
            />
          </div>
        </div>
      </div>

      {/* Steps */}
      {runbook.Steps && runbook.Steps.length > 0 && (
        <div className="card mb-4">
          <div className="card-header bg-success text-white">
            <h5 className="mb-0">Resolution Steps ({runbook.Steps.length})</h5>
          </div>
          <div className="card-body">
            {runbook.Steps.map((step, index) => (
              <div key={index} className="card mb-3">
                <div className="card-body">
                  <div className="row mb-2">
                    <div className="col-md-2">
                      <label className="form-label"><strong>Step #</strong></label>
                      <input
                        type="number"
                        className="form-control bg-light"
                        value={step.step_number || index + 1}
                        readOnly
                        style={{ textAlign: 'center', fontWeight: '600' }}
                      />
                    </div>
                    <div className="col-md-10">
                      <label className="form-label"><strong>Description</strong></label>
                      <textarea
                        className="form-control bg-light"
                        rows="3"
                        value={step.step_description || ''}
                        readOnly
                        style={{ textAlign: 'left', resize: 'vertical', fontSize: '0.875rem', borderRadius: '6px' }}
                      />
                    </div>
                  </div>

                  {/* Image Display Section */}
                  {(step.image_url || step.image_s3_key) && (
                    <div className="row mt-3">
                      <div className="col-md-12">
                        <label className="form-label"><strong>Step Image</strong></label>
                        <div className="border rounded p-3 bg-light">
                          {step.image_url ? (
                            <>
                              <img 
                                src={step.image_url} 
                                alt={`Step ${step.step_number}`}
                                className="img-fluid rounded border"
                                style={{ maxHeight: '400px', maxWidth: '100%', objectFit: 'contain' }}
                                onError={(e) => {
                                  console.error(`[ViewRunbook] Image load failed for step ${step.step_number}:`, {
                                    image_url: step.image_url,
                                    image_s3_key: step.image_s3_key
                                  });
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'block';
                                }}
                                onLoad={() => {
                                  console.log(`[ViewRunbook] Image loaded successfully for step ${step.step_number}`);
                                }}
                              />
                              <div style={{ display: 'none' }} className="alert alert-warning mb-0 mt-2">
                                <i className="bi bi-exclamation-triangle me-2"></i>
                                <strong>Image not available or URL expired</strong>
                                <br />
                                <small className="text-muted">S3 Key: {step.image_s3_key || 'Unknown'}</small>
                              </div>
                              <div className="mt-2">
                                <a 
                                  href={step.image_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  <i className="bi bi-box-arrow-up-right me-1"></i>
                                  View Full Size
                                </a>
                              </div>
                            </>
                          ) : (
                            <div className="alert alert-info mb-0">
                              <i className="bi bi-info-circle me-2"></i>
                              Image reference exists but presigned URL not generated
                              <br />
                              <small className="text-muted">S3 Key: {step.image_s3_key}</small>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="d-flex justify-content-end gap-2 mb-4">
        <button 
          type="button" 
          className="btn btn-secondary" 
          onClick={() => navigate('/runbook_list')}
        >
          <i className="bi bi-arrow-left me-1"></i>
          Back to List
        </button>
      </div>
    </div>
  );
}

export default ViewRunbook;